import { React, useEffect, useState, useCallback, useMemo } from 'react';
import { Row, Col, CardGroup } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import { getDocList, fetchTagsThrottled } from '../../services';
import DocCard from './DocCard';
import DocCardMitreAttack from '../MitreAttack/DocCardMitreAttack';
import AlertMessage from '../AlertMessage';
import SortDocList from './SortDocList';
import { useSelector } from 'react-redux';
import DocListSkeleton from './DocListSkeleton';
import { Metadata } from '../../services/Contexture/Metadata';
import { selectCveFields } from '../../redux/selectors/cveFieldsSelectors';

// Simple in-memory cache so we only fetch cyberReliability metadata
// once per repositoryId across all DocList instances.
const cyberReliabilityCache = {};

const fallbackCyberReliabilityLabels = {
  "1": { "label": "High" },
  "2": { "label": "Medium" },
  "3": { "label": "Low" }
}

const DocList = ({
  profilePrimary,
  activeGroupKey,
  profileSecondary,
  list,
  showAddWatchList,
  showRemove,
}) => {
  const cveFields = useSelector(selectCveFields);
  const PAGE_SIZE = 10;
  const [sortLoader, setSortLoader] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [cyberReliabilityLabels, setCyberReliabilityLabels] = useState(null);
  const [docs, setDocs] = useState({
    items: [],
    nextToken: 0,
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [searchOptions, setSearchOptions] = useState([]);
  const [selectedSearchValue, setSelectedSearchValue] = useState([]);
  const defaultSortValue = ['publishDate-'];
  const [sortValue, setSortValue] = useState(defaultSortValue);

  const fetchTags = useCallback(async (reset=false) => {
    const response = await fetchTagsThrottled({
      profileId: profilePrimary.profileId,
    }, reset);

    if (response) {
      const tags = response.tags || [];

      const searchOpt = tags.map((t) => {
        return { name: t.name, id: t.name };
      })?.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

      setSearchOptions(searchOpt);
    }
  }, [profilePrimary.profileId]);

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      fetchTags();
    }

    return () => {
      isMounted = false;
    };
  }, [fetchTags, profilePrimary.profileId]);

  useEffect(() => {
    const repositoryId = profilePrimary?.repositoryId;
    if (!repositoryId) {
      return;
    }

    // If we have cached labels for this repository, reuse them
    if (cyberReliabilityCache[repositoryId]) {
      setCyberReliabilityLabels(cyberReliabilityCache[repositoryId]);
      return;
    }

    const fetchObjectTypeMetadata = async () => {
      try {
        const response = await Metadata.fetchFields(repositoryId, { field_name: 'cyberReliability' });
        const data = response?.fields?.[0]?.validValues;
        const options = data || [];
        const credibilityMap = options.reduce((acc, item) => {
          acc[item.value] = {
            label: item.label,
          };
          return acc;
        }, {});

        cyberReliabilityCache[repositoryId] = credibilityMap;
        setCyberReliabilityLabels(credibilityMap);
      } catch (error) {
        console.error('Failed to fetch cyberReliability metadata:', error);
        // Fallback to minimal set if metadata fetch fails
        setCyberReliabilityLabels(fallbackCyberReliabilityLabels);
      }
    };

    fetchObjectTypeMetadata();

  }, [profilePrimary?.repositoryId]);


  const fetchPage = useCallback(
    async (pageIndex, isMounted = true) => {
      setIsFetching(true);
      setSortLoader(true);
      const tags = selectedSearchValue.map((t) => t.name);
      const firstResult = pageIndex * PAGE_SIZE + 1;

      getDocList(
        profilePrimary.profileId,
        list,
        sortValue,
        firstResult,
        PAGE_SIZE,
        tags
      ).then((res) => {
        if (!isMounted) return;

        const newItems = res?.documents || [];
        const newTotalHits = res?.totalHits || 0;
        const newPageCount = Math.ceil(newTotalHits / PAGE_SIZE) || 0;

        // If we're on a page index that no longer exists (e.g. deleted last item on last page),
        // move to the last valid page so the user still sees data.
        if (newPageCount > 0 && pageIndex >= newPageCount && pageIndex > 0) {
          setTotalRecords(newTotalHits);
          setIsFetching(false);
          setSortLoader(false);
          const targetPage = newPageCount - 1;
          setCurrentPage(targetPage);
          // Main effect watching currentPage will refetch for the new page.
          return;
        }

        setDocs({
          items: newItems,
          nextToken: res?.lastResult || 0,
        });
        setTotalRecords(newTotalHits);
        setIsFetching(false);
        setSortLoader(false);
      });
    },
    [PAGE_SIZE, profilePrimary.profileId, list, selectedSearchValue, sortValue]
  );

  useEffect(() => {
    let isMounted = true;

    fetchPage(currentPage, isMounted);

    return () => {
      isMounted = false;
    };
  }, [
    profilePrimary.profileId,
    list,
    selectedSearchValue,
    sortValue,
    currentPage,
    fetchPage,
  ]);

  // Listen for refresh events to reload document lists
  useEffect(() => {
    const handleRefresh = () => {
      // Enter loading state and clear current items so cards are hidden
      setIsFetching(true);
      setDocs({ items: [], nextToken: 0 });
      fetchPage(currentPage, true);
    };

    window.addEventListener('refreshDocLists', handleRefresh);

    return () => {
      window.removeEventListener('refreshDocLists', handleRefresh);
    };
  }, [fetchPage, currentPage]);

  // Handle scroll to top when manually changing pages
  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
    window.scrollTo(0, 0);
  };

  const onSelect = (selectedList, selectedItem) => {
    setIsFetching(true);
    setSortLoader(true);
    setCurrentPage(0);
    setSelectedSearchValue([...selectedList]);
  };

  const pageCount = useMemo(
    () => Math.ceil(totalRecords / PAGE_SIZE) || 0,
    [totalRecords, PAGE_SIZE]
  );

  const CardSelector = (props) => {
    if (props.profilePrimary.docType === 'mitre') {
      return <DocCardMitreAttack {...props} />;
    }
    return <DocCard {...props} />;
  };

  return (
    <Row>
      <Col lg={12}>
        <SortDocList
          tagOptions={searchOptions}
          tagSelectedValues={selectedSearchValue}
          onSelect={onSelect}
          setIsFetching={setIsFetching}
          setSortValue={setSortValue}
          showFilter={(list && list !== 'rejectlist')}
          sortSettings={{
            title: {
              label: 'Title',
              direction: 'DESC',
            },
            ...(profilePrimary.docType === 'news' && {
              site: { label: 'Site', direction: 'DESC', active: false },
            }),
            ...(activeGroupKey === 'cve' && {
              cvssBaseScore: {
                label: 'Severity Index',
                direction: 'DESC',
                active: false,
              },
            }),
            ...(activeGroupKey === 'news' && {
              cyberReliability: {
                label: 'Credibility Score',
                direction: 'DESC',
                active: false,
              },
            }),
            publishDate: {
              label: 'Publish Date',
              direction: 'DESC',
              active: true,
            },
          }}
          defaultSortValue={defaultSortValue}
          onSortingChanged={() => setCurrentPage(0)}
        />
      </Col>
      <CardGroup
        className={`ctx-card-list ${sortLoader ? 'sort-loading-overlay' : ''}`}
      >
        {!isFetching && docs.items && docs.items.length ? (
          <>
            {!isFetching && docs.items.map((doc) => (
              <CardSelector
                doc={doc}
                cyberReliabilityLabels={cyberReliabilityLabels}
                showAddWatchList={showAddWatchList}
                list={list}
                showRemove={showRemove}
                profilePrimary={profilePrimary}
                profileSecondary={profileSecondary}
                activeGroupKey={activeGroupKey}
                key={doc.id}
                allTags={searchOptions}
                selectedTags={selectedSearchValue.map((t) => t.name)}
                cveFields={cveFields}
                fetchTags={fetchTags}
              />
            ))}
          </>
        ) : null}

        {isFetching ? <DocListSkeleton />
          : null}

        {pageCount > 1 ? (
          <ReactPaginate
            breakLabel='...'
            nextLabel='Next'
            onPageChange={handlePageChange}
            pageRangeDisplayed={3}
            marginPagesDisplayed={1}
            pageCount={pageCount}
            previousLabel='Previous'
            forcePage={currentPage}
            containerClassName='pagination justify-content-center ctx-pagination'
            pageClassName='page-item'
            pageLinkClassName='page-link'
            previousClassName='page-item'
            previousLinkClassName='page-link'
            nextClassName='page-item'
            nextLinkClassName='page-link'
            activeClassName='active'
            disabledClassName='disabled'
          />
        ) : null}
        {docs.items.length === 0 && isFetching === false ? (
          <AlertMessage variant='danger' message='No Record Found!' />
        ) : null}
      </CardGroup>
    </Row>
  );
};
export default DocList;
